/**
 * Lets a customer attach a photo to the contact form (e.g. for a return or
 * fault). Shopify's native contact form can only ever email name/email/
 * phone/message text - it has no file-attachment support at all, no matter
 * what fields are added to the form. To work around that without a backend
 * or a Shopify App, the photo is uploaded client-side straight to Cloudinary
 * (via an unsigned upload preset - no secret key exposed here), and the
 * resulting image URL is appended to the message body before the form
 * submits normally, so it arrives in the notification email as a link.
 */
class ContactFormPhotoUpload {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this.fileInput = form.querySelector('[data-contact-photo-input]');
    this.errorEl = form.querySelector('[data-contact-photo-error]');
    this.bodyField = form.querySelector('[name="contact[body]"]');
    this.submitButton = form.querySelector('[type="submit"]');
    this.cloudName = form.dataset.cloudinaryCloudName;
    this.uploadPreset = form.dataset.cloudinaryUploadPreset;
    this.maxBytes = 5 * 1024 * 1024;
    this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    this.originalSubmitLabel = null;

    if (!this.fileInput || !this.bodyField || !this.cloudName || !this.uploadPreset) return;

    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    const file = this.fileInput.files[0];
    if (!file) return;

    event.preventDefault();
    this.clearError();

    if (!this.allowedTypes.includes(file.type)) {
      this.showError('Please attach a JPG, PNG, WEBP, or HEIC image.');
      return;
    }

    if (file.size > this.maxBytes) {
      this.showError('That photo is too large (max 5MB). Please choose a smaller file.');
      return;
    }

    this.setLoading(true);

    try {
      const url = await this.uploadToCloudinary(file);
      const existing = this.bodyField.value.trim();
      this.bodyField.value = existing ? `${existing}\n\nAttached photo: ${url}` : `Attached photo: ${url}`;

      // Clear the file so the next submit (triggered below) passes straight
      // through instead of re-entering this handler.
      this.fileInput.value = '';
      this.form.submit();
    } catch (error) {
      this.setLoading(false);
      this.showError('Sorry, that photo could not be uploaded. Please try again, or send your message without it.');
    }
  }

  /**
   * @param {File} file
   * @returns {Promise<string>}
   */
  async uploadToCloudinary(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.uploadPreset);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${this.cloudName}/image/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) throw new Error('Cloudinary upload failed');

    const data = await response.json();
    if (!data.secure_url) throw new Error('Cloudinary upload response missing secure_url');

    return data.secure_url;
  }

  /**
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    if (!this.submitButton) return;

    if (isLoading) {
      this.originalSubmitLabel = this.submitButton.textContent;
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Uploading photo…';
    } else {
      this.submitButton.disabled = false;
      if (this.originalSubmitLabel !== null) {
        this.submitButton.textContent = this.originalSubmitLabel;
      }
    }
  }

  /**
   * @param {string} message
   */
  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }
}

document.querySelectorAll('[data-contact-form-photo]').forEach((form) => new ContactFormPhotoUpload(form));
